import React, { useState, useEffect, useCallback } from "react";
import { adminLiquidityApi } from "../../services/api";

/**
 * Tab Statistiche del pannello admin liquidità.
 * Mostra stato pausa, riepilogo settimanale e grafico barre giornaliero (ultimi 30gg).
 */
const AdminLiquidityStats = ({ adminKey }) => {
  const [state, setState] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resuming, setResuming] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [stateData, statsData] = await Promise.all([
        adminLiquidityApi.getState(adminKey),
        adminLiquidityApi.getWeeklyStats(adminKey),
      ]);
      setState(stateData);
      setStats(statsData);
    } catch (e) {
      setError(e.message || "Errore caricamento statistiche");
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleResume = async () => {
    if (!window.confirm("Are you sure you want to resume the liquidity program?")) return;
    setResuming(true);
    try {
      await adminLiquidityApi.resume(adminKey);
      await fetchData();
    } catch (e) {
      alert("Error during resume: " + e.message);
    } finally {
      setResuming(false);
    }
  };

  if (loading) return <div style={s.loading}>Loading statistics...</div>;
  if (error) return <div style={s.error}>⚠️ {error}</div>;

  const weekly = stats?.currentWeek;
  const pct = weekly
    ? Math.min(
        100,
        Math.round((parseFloat(weekly.total) / parseFloat(weekly.weeklyMax)) * 100)
      )
    : 0;

  const barColor =
    state?.is_paused || pct >= 100
      ? "#e53e3e"
      : pct >= 75
      ? "#dd6b20"
      : "#38a169";

  const dailyMax = stats?.daily?.length
    ? Math.max(...stats.daily.map((d) => parseFloat(d.daily_eth) || 0))
    : 1;

  return (
    <div style={s.container}>
      {/* Status badge + pulsante resume */}
      <div style={s.statusRow}>
        <div
          style={{
            ...s.badge,
            backgroundColor: state?.is_paused ? "#fed7d7" : "#c6f6d5",
            color: state?.is_paused ? "#c53030" : "#276749",
          }}
        >
          {state?.is_paused ? "🔴 Program PAUSED" : "🟢 Program ACTIVE"}
        </div>
        {state?.is_paused && (
          <button
            onClick={handleResume}
            disabled={resuming}
            style={{ ...s.btn, ...s.btnGreen, opacity: resuming ? 0.6 : 1 }}
          >
            {resuming ? "Resuming..." : "▶ Resume Program"}
          </button>
        )}
        <button onClick={fetchData} style={s.btnRefresh}>
          🔄 Refresh
        </button>
      </div>

      {state?.is_paused && state.paused_reason && (
        <div style={s.pauseInfo}>
          <strong>Pause reason:</strong> {state.paused_reason}
          {state.paused_at && (
            <span style={{ marginLeft: 12, color: "#718096", fontSize: 13 }}>
              ({new Date(state.paused_at).toLocaleString("en-US")})
            </span>
          )}
        </div>
      )}

      {/* Weekly summary */}
      {weekly && (
        <div style={s.card}>
          <h3 style={s.cardTitle}>Current Weekly Liquidity</h3>

          <div style={s.progressTrack}>
            <div
              style={{
                ...s.progressBar,
                width: `${pct}%`,
                backgroundColor: barColor,
              }}
            />
          </div>

          <div style={s.statsRow}>
            <StatBox label="Used" value={`${parseFloat(weekly.total).toFixed(4)} ETH`} />
            <StatBox label="Limit" value={`${weekly.weeklyMax} ETH`} />
            <StatBox label="Warning threshold" value={`${weekly.weeklyWarning} ETH`} />
            <StatBox label="Events" value={weekly.eventCount} />
            <StatBox label="Usage" value={`${pct}%`} accent={barColor} />
          </div>
        </div>
      )}

      {/* Grafico barre giornaliero */}
      {stats?.daily?.length > 0 && (
        <div style={s.card}>
          <h3 style={s.cardTitle}>Daily Activity (last 30 days)</h3>
          <div style={s.chartContainer}>
            {[...stats.daily].reverse().map((day) => {
              const eth = parseFloat(day.daily_eth) || 0;
              const heightPct = dailyMax > 0 ? (eth / dailyMax) * 100 : 0;
              return (
                <div key={day.day} style={s.barWrapper} title={`${day.day}\n${eth.toFixed(4)} ETH\n${day.event_count} eventi`}>
                  <div
                    style={{
                      ...s.bar,
                      height: `${Math.max(heightPct, 2)}%`,
                      backgroundColor: "#38a169",
                    }}
                  />
                  <div style={s.barLabel}>
                    {day.day.slice(5)} {/* mm-dd */}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={s.chartNote}>Bar height is proportional to daily ETH. Hover for details.</div>
        </div>
      )}
    </div>
  );
};

const StatBox = ({ label, value, accent }) => (
  <div style={s.statBox}>
    <div style={{ ...s.statValue, color: accent || "#2c3e50" }}>{value}</div>
    <div style={s.statLabel}>{label}</div>
  </div>
);

const s = {
  container: { padding: "0 4px" },
  loading: { padding: 24, color: "#718096", textAlign: "center" },
  error: { padding: 16, color: "#c53030", backgroundColor: "#fff5f5", borderRadius: 6 },
  statusRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  badge: {
    padding: "8px 16px",
    borderRadius: 20,
    fontWeight: 600,
    fontSize: 14,
  },
  pauseInfo: {
    backgroundColor: "#fff5f5",
    border: "1px solid #feb2b2",
    borderRadius: 6,
    padding: "10px 14px",
    marginBottom: 16,
    fontSize: 14,
    color: "#c53030",
  },
  btn: {
    padding: "8px 16px",
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500,
  },
  btnGreen: { backgroundColor: "#27ae60", color: "white" },
  btnRefresh: {
    padding: "8px 14px",
    backgroundColor: "#edf2f7",
    border: "1px solid #cbd5e0",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 13,
    marginLeft: "auto",
  },
  card: {
    backgroundColor: "white",
    borderRadius: 8,
    padding: 20,
    marginBottom: 20,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: "#2c3e50",
    marginBottom: 14,
    marginTop: 0,
  },
  progressTrack: {
    backgroundColor: "#edf2f7",
    borderRadius: 6,
    height: 14,
    marginBottom: 16,
    overflow: "hidden",
  },
  progressBar: {
    height: "100%",
    borderRadius: 6,
    transition: "width 0.5s ease",
  },
  statsRow: {
    display: "flex",
    gap: 16,
    flexWrap: "wrap",
  },
  statBox: {
    flex: "1 1 100px",
    backgroundColor: "#f7fafc",
    borderRadius: 6,
    padding: "10px 14px",
    textAlign: "center",
  },
  statValue: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: "#718096",
  },
  chartContainer: {
    display: "flex",
    alignItems: "flex-end",
    gap: 4,
    height: 140,
    padding: "0 4px",
    overflowX: "auto",
  },
  barWrapper: {
    flex: "1 0 18px",
    minWidth: 18,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 2,
    cursor: "default",
  },
  bar: {
    width: "100%",
    borderRadius: "2px 2px 0 0",
    minHeight: 2,
  },
  barLabel: {
    fontSize: 9,
    color: "#a0aec0",
    transform: "rotate(-45deg)",
    whiteSpace: "nowrap",
    transformOrigin: "top center",
    marginTop: 4,
    height: 28,
  },
  chartNote: {
    fontSize: 11,
    color: "#a0aec0",
    marginTop: 8,
    textAlign: "center",
  },
};

export default AdminLiquidityStats;
